/**
 * Buyer-side fee figures shown on listing surfaces before an offer exists.
 *
 * Mirrors the backend's `DEFAULT_BUYER_PRICING` (apps/homepoint-api/src/utils/
 * buyerBrokerFee.ts): a 1.25% base rate, +0.25% when the offer carries an
 * inspection, and a $5,000 minimum. A financed offer with an inspection is the
 * common case, so the browse estimate uses that 1.5% effective rate. The real
 * fee is computed per offer on the backend; this is the honest preview of it.
 */
export const BUYER_FEE_RATE = 0.015;
export const BUYER_FEE_MINIMUM = 5000;
export const TYPICAL_BUYER_COMMISSION_RATE = 0.03;

export interface BuyingPowerEstimate {
  /** Homepoint's estimated fee on this home, in dollars with cents. */
  homepointFee: number;
  /** True when the $5,000 minimum applies (home price under ~$333K). */
  feeIsMinimum: boolean;
  /** A typical 3% buyer-agent commission on this home. */
  typicalCommission: number;
  /** The gap between the two, with cents: what the buyer can negotiate with. Never negative. */
  buyingPower: number;
}

/**
 * Rounded to cents exactly as the backend's `calculateBuyerBrokerFee` does
 * (`_.round(price * rate, 2)`), so the two agree to the cent. Callers round
 * only when formatting, so the displayed buying power is the rounded
 * difference rather than a difference of rounded parts.
 */
/**
 * Round to cents with the decimal-shift Lodash's `_.round(x, 2)` uses, so a
 * value like 333,335 × 1.5% lands on the same cent the backend computes
 * (multiplying by 100 first can differ by a cent through float representation).
 */
const toCents = (amount: number): number => {
  if (!Number.isFinite(amount)) return 0;
  const shifted = Math.round(Number(`${amount}e2`));
  return Number(`${shifted}e-2`);
};

export const estimateBuyerBrokerFee = (price: number): number => {
  if (!price || price <= 0) return 0;
  return Math.max(BUYER_FEE_MINIMUM, toCents(price * BUYER_FEE_RATE));
};

export const estimateBuyingPower = (price: number): BuyingPowerEstimate => {
  const safePrice = price > 0 ? price : 0;
  const homepointFee = estimateBuyerBrokerFee(safePrice);
  const typicalCommission = toCents(safePrice * TYPICAL_BUYER_COMMISSION_RATE);
  return {
    homepointFee,
    feeIsMinimum: homepointFee === BUYER_FEE_MINIMUM && safePrice > 0,
    typicalCommission,
    buyingPower: Math.max(typicalCommission - homepointFee, 0),
  };
};

const currency = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

/**
 * The offer terms the browse estimate assumes, spelled out wherever the
 * figure is shown. The exact fee is set per offer by the backend: cash is
 * -0.25%, VA +0.25%, a home-sale contingency +0.25%, and dropping the
 * inspection -0.25%, all against the same $5,000 minimum.
 */
export const FEE_ESTIMATE_ASSUMPTIONS =
  'Estimate assumes a financed offer with an inspection (1.25% base + 0.25%), ' +
  '$5,000 minimum. Cash is 0.25% less, VA and a home-sale contingency 0.25% ' +
  'more each. Your exact fee is set when you make an offer.';

/**
 * One-line explanation of the fee comparison, e.g.
 * "Est. Homepoint fee $8,999 (1.5%, financed with inspection) vs $17,997 (3%)"
 * or, under the minimum, "Est. Homepoint fee $5,000 (minimum) vs $7,500 (3%)".
 */
export const describeFeeComparison = (
  estimate: BuyingPowerEstimate
): string => {
  const typical = `${currency.format(estimate.typicalCommission)} (3%)`;
  const basis = estimate.feeIsMinimum
    ? 'minimum'
    : '1.5%, financed with inspection';
  return `Est. Homepoint fee ${currency.format(
    estimate.homepointFee
  )} (${basis}) vs ${typical}`;
};

/**
 * The short form the listing dialog's action card uses, e.g.
 * "Homepoint fee $8,999 (1.5%) vs $17,997 (3%)" or, under the minimum,
 * "Homepoint fee $5,000 (minimum) vs $7,500 (3%)".
 */
export const describeFeeComparisonShort = (
  estimate: BuyingPowerEstimate
): string => {
  const basis = estimate.feeIsMinimum ? 'minimum' : '1.5%';
  return `Homepoint fee ${currency.format(
    estimate.homepointFee
  )} (${basis}) vs ${currency.format(estimate.typicalCommission)} (3%)`;
};
